/**
 * Combat formulas, shared so the client can show the odds ("62% to hit, max 34") that the
 * server will actually use. RuneScape-shaped: an accuracy roll against a defence roll, then
 * a damage roll up to a max hit. Hitpoints are Vitality × 10.
 */
import type { CharacterState } from './character.js';
import { GEAR, SLOTS, gearDef, isGearId, type CombatStyle, type GearDef, type GearId, type Perk, type Stance } from './content/equipment.js';
import { SHARED_RARE, SHARED_RARE_CHANCE, TIER_CHANCE, type CreatureDef, type Drop, type LootTable, type Rarity } from './content/creatures.js';
import { SKILL_NAMES } from './content/skills.js';
import { level, type Blocker } from './rules.js';
import type { ItemId } from './content/items.js';

/** Hitpoints: Vitality × 10, plus 20 for a hearty amulet. */
export const maxHp = (c: Pick<CharacterState, 'xp'> & { equip?: CharacterState['equip'] }) => level(c, 'vitality') * 10 + (hasPerk(c, 'hearty') ? 20 : 0) + (hasPerk(c, 'radiant') ? 30 : 0);
/** Is a worn ring or amulet giving this perk? */
export function hasPerk(c: { equip?: CharacterState['equip'] }, perk: Perk) { for (const s of ['ring', 'neck'] as const) { const id = c.equip?.[s]; if (id && gearDef(id).perk === perk) return true; } return false; }

/** Fists, when nothing is wielded. */
export const UNARMED: GearDef = { name: 'Fists', slot: 'weapon', req: ['melee', 1], acc: 0, str: 0, speed: 4, range: 1, style: 'melee', metal: 1, desc: '' };
export function weaponOf(c: Pick<CharacterState, 'equip'>): GearDef { const id = c.equip.weapon; return id && !gearDef(id).broken ? gearDef(id) : UNARMED; }

/** Best arrows in the pack that this character can use with their bow. */
export function bestArrows(c: Pick<CharacterState, 'inv' | 'xp'>): GearId | null {
  let best: GearId | null = null;
  for (const id of c.inv) {
    if (!id || !isGearId(id)) continue;
    const g = GEAR[id];
    if (g.slot !== 'ammo' || level(c, g.req[0]) < g.req[1]) continue;
    if (!best || (g.str ?? 0) > (GEAR[best].str ?? 0)) best = id;
  }
  return best;
}

export function equipBlocker(c: Pick<CharacterState, 'xp'>, id: GearId): Blocker | null {
  const g = gearDef(id);
  if (g.broken) return { text: `Your ${g.name.replace(' (broken)', '').toLowerCase()} is broken. Bram can repair it, or repair it yourself at an anvil.`, short: 'broken' };
  if (level(c, g.req[0]) < g.req[1]) return { text: `You need ${SKILL_NAMES[g.req[0]]} ${g.req[1]} to use the ${g.name.toLowerCase()}.`, short: `${SKILL_NAMES[g.req[0]]} ${g.req[1]}` };
  return null;
}

/** Sum of a bonus across worn gear. */
function worn(c: Pick<CharacterState, 'equip'>, k: 'acc' | 'str' | 'def' | 'racc') { let t = 0; for (const s of SLOTS) { const id = c.equip[s]; if (id) t += gearDef(id)[k] ?? 0; } return t; }

export function hitChance(att: number, def: number) {
  return att > def ? 1 - (def + 2) / (2 * (att + 1)) : att / (2 * (def + 1));
}

const stanceBonus = (s: Stance) => (s === 'guard' ? 3 : 0);

export interface PlayerAttack { style: CombatStyle; acc: number; max: number; speed: number; range: number; arrows: GearId | null }
/** Everything about this character's next attack. */
export function playerAttack(c: CharacterState): PlayerAttack {
  const w = weaponOf(c), style = w.style ?? 'melee';
  const skill = style === 'ranged' ? 'ranged' : 'melee';
  const L = level(c, skill) + (c.stance === 'attack' ? 3 : 0);
  const arrows = style === 'ranged' ? bestArrows(c) : null;
  const accBonus = worn(c, 'acc') + (style === 'ranged' ? worn(c, 'racc') : 0);
  const strBonus = style === 'ranged' ? GEAR[arrows ?? 'bronze_arrows'].str ?? 0 : worn(c, 'str');
  let acc = (L + 8) * (accBonus + 64);
  let max = Math.max(1, Math.round(((L + 8) * (strBonus + 64)) / 64));
  if (c.buffs.might && style === 'melee') max = Math.round(max * 1.1);
  if (c.buffs.aim && style === 'ranged') acc = Math.round(acc * 1.1);
  return { style, acc, max, speed: w.speed ?? 4, range: w.range ?? 1, arrows };
}
export function playerDefence(c: CharacterState) {
  return (level(c, 'defence') + 8 + stanceBonus(c.stance)) * (worn(c, 'def') + 64);
}
export const creatureAttackRoll = (d: CreatureDef) => (d.att + 8) * (d.acc + 64);
export const creatureDefence = (d: CreatureDef) => (d.def + 8) * (d.defBonus + 64);

/** RuneScape-style combat level shown on name tags. */
export function combatLevel(c: Pick<CharacterState, 'xp'>) {
  return Math.max(3, Math.floor((level(c, 'defence') + level(c, 'vitality')) / 4 + Math.max(level(c, 'melee'), level(c, 'ranged')) * 0.65));
}

/** How an enemy's level compares with yours, for colouring its level tag. */
export type Danger = 'easy' | 'even' | 'risky' | 'deadly';
export function dangerOf(mine: number, theirs: number): Danger {
  const d = theirs - mine;
  return d <= -6 ? 'easy' : d <= 2 ? 'even' : d <= 9 ? 'risky' : 'deadly';
}
/** RuneScape's rule: aggressive enemies leave alone anyone more than twice their level. */
export const outgrows = (playerCombat: number, enemyLevel: number) => playerCombat > enemyLevel * 2;

/** Combat xp for `dmg` damage: the trained skill gets 4 per 10 damage, Vitality a third of that. */
export function combatXp(dmg: number) { return { main: dmg * 0.4, vitality: dmg * 0.133 }; }

/* ---------------- loot ---------------- */
export interface LootDrop { item: ItemId | 'coins'; n: number; rarity: Rarity }
function pick(list: Drop[], rng: () => number): Drop {
  const total = list.reduce((a, d) => a + (d.w ?? 1), 0);
  let r = rng() * total;
  for (const d of list) { r -= d.w ?? 1; if (r <= 0) return d; }
  return list[list.length - 1];
}
const count = (d: Drop, rng: () => number) => (d.n ? d.n[0] + Math.floor(rng() * (d.n[1] - d.n[0] + 1)) : 1);

/** Roll a creature's drops. Always-drops, then `rolls` rarity rolls, then the shared rare table. */
export function rollLoot(t: LootTable, rng: () => number): LootDrop[] {
  const out: LootDrop[] = t.always.map(d => ({ item: d.item, n: count(d, rng), rarity: 'always' as const }));
  for (let i = 0; i < t.rolls; i++) {
    let r = rng(), tier: keyof typeof TIER_CHANCE = 'common';
    for (const k of ['very_rare', 'rare', 'uncommon', 'common'] as const) { if (r < TIER_CHANCE[k]) { tier = k; break; } r -= TIER_CHANCE[k]; }
    const list = t[tier];
    if (list?.length) { const d = pick(list, rng); out.push({ item: d.item, n: count(d, rng), rarity: tier }); }
  }
  if (rng() < SHARED_RARE_CHANCE) { const d = pick(SHARED_RARE, rng); out.push({ item: d.item, n: count(d, rng), rarity: 'shared' }); }
  return out;
}

/** "Acc +8 · Str +7 · fast" for shop and pack tooltips. */
export function gearStats(id: GearId) {
  const g = gearDef(id), parts: string[] = [];
  if (g.slot === 'weapon') { parts.push(`Acc +${g.acc ?? 0}`); if (g.style !== 'ranged') parts.push(`Str +${g.str ?? 0}`); parts.push(g.speed! <= 4 ? 'fast' : 'slow'); if (g.style === 'ranged') parts.push(`range ${g.range}`); }
  else if (g.slot === 'ammo') parts.push(`Ranged str +${g.str}`);
  else if (g.slot === 'ring' || g.slot === 'neck') { for (const [k, n] of [['acc', 'Acc'], ['str', 'Str'], ['def', 'Def']] as const) if (g[k]) parts.push(`${n} +${g[k]}`); }
  else { parts.push(`Def +${g.def ?? 0}`); if (g.racc) parts.push(`Ranged acc +${g.racc}`); }
  return parts.join(' · ');
}
